import type { StrategyState } from '../../strategy-state';

export const STRATEGY_CANONICAL_SNAPSHOT_SCHEMA_VERSION =
  'strategy_canonical_snapshot_v1' as const;

export type StrategyConsumerId =
  | 'ask_helix_workspace_context'
  | 'strategy_ontology_projection'
  | 'workforce_reduction_strategy_protection'
  | 'org_analysis_strategy_context'
  | 'composite_outcome_planner';

export type StrategyConsumerRead =
  | 'strategy_pack_summary'
  | 'neurotruth_pack'
  | 'strategy_ontology_projection'
  | 'strategy_state'
  | 'required_capability_ids'
  | 'constraints'
  | 'design_principles';

export type StrategyConsumerWritePolicy =
  | 'read_only'
  | 'versioned_commit_only';

export type StrategyConsumer = {
  consumerId: StrategyConsumerId;
  reads: readonly StrategyConsumerRead[];
  writePolicy: StrategyConsumerWritePolicy;
  mustNotBreak: boolean;
  ownerModule: string;
  compatibilityTest: string;
};

export type StrategyCanonicalSnapshotPackStatus =
  | 'available'
  | 'missing'
  | 'stale'
  | 'permission_denied';

export type StrategyCanonicalSnapshotProjectionStatus =
  | 'fresh'
  | 'missing'
  | 'stale';

export type StrategyFinalizedCorpusCoverage = Readonly<{
  sourceCounts: Readonly<{
    strategies: number;
    constraints: number;
    principles: number;
    capabilities: number;
  }>;
  summarizedCount: number;
  projectedObjectCount: number;
  projectedRelationshipCount: number;
  retainedOnlyCount: number;
  unresolvedCount: number;
  unsupportedCount: number;
  sourceDispositionDigest: string;
}>;

/** Summary-sized identity for an immutable retained source corpus; it is not an approval record. */
export type StrategyFinalizedCorpusLocator = Readonly<{
  strategyId: string;
  packId: string;
  finalVersionId: string;
  sourceSchemaVersion: 'neurotruth_canonical_source_snapshot_v1';
  sourceDigest: string;
  corpusDigest: string;
  summaryDigest: string;
  projectionPayloadDigest: string;
  summaryRevision: number;
  projectionVersion: 'strategy_finalized_corpus_projection_v1';
  coverageDigest: string;
  coverage: StrategyFinalizedCorpusCoverage;
  objectCount: number;
  relationshipCount: number;
  sourceTruth: 'retained_finalized_version_source_not_approval';
}>;

export type StrategyCanonicalSnapshotGapCode =
  | 'active_strategy_pointer_missing'
  | 'active_strategy_record_missing'
  | 'active_strategy_pack_missing'
  | 'active_strategy_pack_version_missing'
  | 'active_strategy_pack_stale'
  | 'active_strategy_state_missing'
  | 'ontology_projection_missing'
  | 'ontology_projection_final_version_missing'
  | 'ontology_projection_adapter_version_missing'
  | 'ontology_projection_projected_metadata_missing'
  | 'ontology_projection_mixed_versions'
  | 'ontology_projection_version_mismatch'
  | 'ontology_projection_objectives_missing'
  | 'ontology_projection_constraints_missing'
  | 'ontology_projection_capabilities_missing'
  | 'ontology_projection_relationships_missing';

export type StrategyCanonicalSnapshotGap = {
  code: StrategyCanonicalSnapshotGapCode;
  message: string;
  recovery: string;
};

export type StrategyCanonicalSnapshotActivePack =
  | {
      status: 'available' | 'stale';
      strategyId: string;
      packId: string;
      versionId: string;
      summary: string;
      asOf?: string | null;
      gaps: StrategyCanonicalSnapshotGap[];
    }
  | {
      status: 'missing' | 'permission_denied';
      strategyId: string | null;
      packId?: string | null;
      versionId?: string | null;
      summary?: string | null;
      asOf?: string | null;
      gaps: StrategyCanonicalSnapshotGap[];
    };

export type StrategyCanonicalSnapshotStateFields = Pick<
  StrategyState,
  | 'strategyStateId'
  | 'version'
  | 'priorities'
  | 'constraints'
  | 'requiredCapabilityIds'
  | 'narrative'
>;

export type StrategyCanonicalSnapshotState =
  | ({
      status: 'available';
      asOf?: string | null;
      gaps: StrategyCanonicalSnapshotGap[];
    } & StrategyCanonicalSnapshotStateFields)
  | {
      status: 'missing' | 'permission_denied';
      strategyStateId: null;
      version: null;
      priorities: null;
      constraints: null;
      requiredCapabilityIds: [];
      narrative: null;
      asOf?: string | null;
      gaps: StrategyCanonicalSnapshotGap[];
    };

export type StrategyCanonicalSnapshotProjection = {
  projectionStatus: StrategyCanonicalSnapshotProjectionStatus;
  projectedVersionId: string | null;
  asOf?: string | null;
  objectiveRefs: string[];
  constraintRefs: string[];
  capabilityRefs: string[];
  finalizedCorpusLocator?: StrategyFinalizedCorpusLocator | null;
  relationshipContext?: import('./strategy-relationship-context').StrategyRelationshipContext;
  gaps: StrategyCanonicalSnapshotGap[];
};

export type StrategyCanonicalSnapshotConsumerView = {
  consumerId: StrategyConsumerId;
  reads: readonly StrategyConsumerRead[];
  writePolicy: 'read_only';
  compatibilityRequired: boolean;
};

export type StrategyCanonicalSnapshot = {
  schemaVersion: typeof STRATEGY_CANONICAL_SNAPSHOT_SCHEMA_VERSION;
  tenantId: string;
  activeStrategyPack: StrategyCanonicalSnapshotActivePack;
  activeStrategyState: StrategyCanonicalSnapshotState;
  ontologyProjection: StrategyCanonicalSnapshotProjection;
  consumers: StrategyCanonicalSnapshotConsumerView[];
  gaps: StrategyCanonicalSnapshotGap[];
};

export const STRATEGY_CONSUMER_REGISTRY = Object.freeze([
  {
    consumerId: 'ask_helix_workspace_context',
    reads: ['strategy_pack_summary'],
    writePolicy: 'read_only',
    mustNotBreak: true,
    ownerModule: 'ask-helix',
    compatibilityTest:
      'apps/api/src/ask-helix/__tests__/workspace-context-source.spec.ts',
  },
  {
    consumerId: 'strategy_ontology_projection',
    reads: ['neurotruth_pack', 'design_principles'],
    writePolicy: 'versioned_commit_only',
    mustNotBreak: true,
    ownerModule: 'ontology',
    compatibilityTest:
      'apps/api/src/ontology/strategy-ontology-projection.service.spec.ts',
  },
  {
    consumerId: 'workforce_reduction_strategy_protection',
    reads: ['strategy_state', 'required_capability_ids'],
    writePolicy: 'read_only',
    mustNotBreak: true,
    ownerModule: 'scenarios/workforce-reduction',
    compatibilityTest:
      'apps/api/src/scenarios/workforce-reduction/workforce-reduction.service.spec.ts',
  },
  {
    consumerId: 'org_analysis_strategy_context',
    reads: ['strategy_state', 'constraints', 'design_principles'],
    writePolicy: 'read_only',
    mustNotBreak: true,
    ownerModule: 'org-analysis',
    compatibilityTest:
      'apps/api/src/strategy-state/strategy-state.schema.spec.ts',
  },
  {
    consumerId: 'composite_outcome_planner',
    reads: [
      'strategy_pack_summary',
      'strategy_ontology_projection',
      'constraints',
    ],
    writePolicy: 'read_only',
    mustNotBreak: true,
    ownerModule: 'ask-helix/composite',
    compatibilityTest:
      'apps/api/src/ask-helix/__tests__/planner-decision.intent-cases.spec.ts',
  },
] as const satisfies readonly StrategyConsumer[]);

export function listStrategyConsumers(): readonly StrategyConsumer[] {
  return STRATEGY_CONSUMER_REGISTRY;
}

export function buildStrategyCanonicalSnapshotConsumerViews(): Array<
  StrategyCanonicalSnapshotConsumerView
> {
  return STRATEGY_CONSUMER_REGISTRY.map((consumer) => ({
    consumerId: consumer.consumerId,
    reads: consumer.reads,
    writePolicy: 'read_only',
    compatibilityRequired: consumer.mustNotBreak,
  }));
}
